import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type DepartmentItem = { id: string; name: string };
type ChecklistItem = { id: string; name: string };
type UserItem = { id: string; name: string };

export type ChecklistExecutionHistorySearch = {
  offset: number;
  limit: number;
  checklistId?: string;
  departmentId?: string;
  userId?: string;
};

export function ChecklistExecutionHistoryFilter() {
  const nav = useNavigate({ from: "/app/checklists/execution-history" });
  const search = useSearch({
    from: "/app/checklists/execution-history",
  }) as ChecklistExecutionHistorySearch;

  const checklistId = search.checklistId ?? "";
  const departmentId = search.departmentId ?? "";
  const userId = search.userId ?? "";

  const hasFilter = !!checklistId || !!departmentId || !!userId;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <InfiniteCombobox<DepartmentItem>
            queryKey={["checklists-history-departments"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/checklists/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/checklists/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentId}
            onValueChange={(val) => {
              nav({
                search: {
                  ...search,
                  offset: 0,
                  departmentId: val || undefined,
                  checklistId: undefined,
                  userId: undefined,
                },
              });
            }}
            placeholder="ພະແນກ"
            className="h-8 w-full sm:w-56"
            clearable
          />
          <InfiniteCombobox<ChecklistItem>
            queryKey={["checklists-history-checklists", departmentId]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/checklists/lookup`,
                args,
                departmentId ? { departmentId } : undefined,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/checklists/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={checklistId}
            onValueChange={(val) => {
              nav({
                search: {
                  ...search,
                  offset: 0,
                  checklistId: val || undefined,
                },
              });
            }}
            placeholder="Checklist"
            className="h-8 w-full sm:w-56"
            clearable
          />
          <InfiniteCombobox<UserItem>
            queryKey={["checklists-history-users", departmentId]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/checklists/users/lookup`,
                args,
                departmentId ? { departmentId } : undefined,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/checklists/users/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={userId}
            onValueChange={(val) => {
              nav({
                search: {
                  ...search,
                  offset: 0,
                  userId: val || undefined,
                },
              });
            }}
            placeholder="ພະນັກງານ"
            className="h-8 w-full sm:w-56"
            clearable
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  checklistId: undefined,
                  departmentId: undefined,
                  userId: undefined,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
